import type { AdvancedCreateFromFileSettings } from '../../types';
import { Body, Col, IconButton, Link, ModalHeader, Row, Slider, Stack, TextField } from '@workramp/ui';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';
import { DEFAULT_ADVANCED_SETTINGS } from '../constants';
import { CreateFromFileSettingsModalBody } from './CreateFromFileSettings.styles';

export interface CreateFromFileSettingsProps {
  isCreating: boolean;
  initialAdvancedSettings?: AdvancedCreateFromFileSettings;
  updateSettings: (advancedSettings: AdvancedCreateFromFileSettings) => void;
  onNavigateBack: () => void;
}

export function CreateFromFileSettings({
  initialAdvancedSettings = DEFAULT_ADVANCED_SETTINGS,
  updateSettings,
  onNavigateBack,
  isCreating,
}: CreateFromFileSettingsProps) {
  const TRANSLATED_BACK = I18n.t('js.common.modal.back');
  const TRANSLATED_TITLE = I18n.t('js.admin.content_creation.advanced_settings.title');
  const TRANSLATED_GUIDE_DETAIL = I18n.t('js.admin.content_creation.advanced_settings.guide_detail');
  const TRANSLATED_INTERACTIVITY = I18n.t('js.admin.content_creation.advanced_settings.interactivity');
  const TRANSLATED_ADDITIONAL_PROMPT = I18n.t('js.admin.content_creation.advanced_settings.additional_prompt');
  const TRANSLATED_ADDITIONAL_PROMPT_NOTE = I18n.t(
    'js.admin.content_creation.advanced_settings.additional_prompt_note',
  );
  const TRANSLATED_ADDITIONAL_PROMPT_PLACEHOLDER = I18n.t(
    'js.admin.content_creation.advanced_settings.additional_prompt_placeholder',
  );
  const TRANSLATED_GUIDE_DETAIL_LOWEST = I18n.t('js.admin.content_creation.advanced_settings.guide_detail_lowest');
  const TRANSLATED_GUIDE_DETAIL_HIGHEST = I18n.t('js.admin.content_creation.advanced_settings.guide_detail_highest');
  const TRANSLATED_INTERACTIVITY_LOWEST = I18n.t('js.admin.content_creation.advanced_settings.interactivity_lowest');
  const TRANSLATED_INTERACTIVITY_HIGHEST = I18n.t('js.admin.content_creation.advanced_settings.interactivity_highest');
  const TRANSLATED_GIVE_FEEDBACK_LINK_TEXT = I18n.t(
    'js.admin.content_creation.advanced_settings.give_feedback_link_text',
  );

  const FEEDBACK_LINK =
    'https://docs.google.com/forms/d/e/1FAIpQLScph488bgcFasvIK42QIKMBw09ofF_wU7SONGhrxExIBi7e2w/viewform';

  const theme = useTheme();

  const [guideDetail, setGuideDetail] = useState(initialAdvancedSettings.guideDetail - 3);
  const [interactivity, setInteractivity] = useState(initialAdvancedSettings.interactivity - 3);
  const [additionalPrompt, setAdditionalPrompt] = useState(initialAdvancedSettings.additionalPrompt);

  const showAdditionalPrompt = false;

  const handleUpdateGuideDetail = (value: number) => {
    setGuideDetail(value);
    updateSettings({
      guideDetail: value + 3,
      interactivity: interactivity + 3,
      additionalPrompt,
    });
  };

  const handleUpdateInteractivity = (value: number) => {
    setInteractivity(value);
    updateSettings({
      guideDetail: guideDetail + 3,
      interactivity: value + 3,
      additionalPrompt,
    });
  };

  const handleUpdateAdditionalPrompt = (value: string) => {
    setAdditionalPrompt(value);
    updateSettings({
      guideDetail,
      interactivity,
      additionalPrompt: value,
    });
  };

  return (
    <>
      <ModalHeader
        title={
          <>
            <IconButton
              variant="text"
              color="secondary"
              iconName="arrow-left:regular"
              label={TRANSLATED_BACK}
              onPress={onNavigateBack}
              isDisabled={isCreating}
              size="sm"
            />
            &nbsp;{TRANSLATED_TITLE}
          </>
        }
      />
      <CreateFromFileSettingsModalBody>
        <Stack size={6}>
          <Stack size={2}>
            <Slider
              isDisabled={isCreating}
              minValue={-2}
              maxValue={2}
              value={guideDetail}
              label={TRANSLATED_GUIDE_DETAIL}
              onChange={(value) => typeof value === 'number' && handleUpdateGuideDetail(value)}
            />
            <Row justify="space-between">
              <Col xs="auto">
                <Body level="sm" color={theme.colors.secondary[600]}>
                  {TRANSLATED_GUIDE_DETAIL_LOWEST}
                </Body>
              </Col>
              <Col xs="auto">
                <Body level="sm" color={theme.colors.secondary[600]}>
                  {TRANSLATED_GUIDE_DETAIL_HIGHEST}
                </Body>
              </Col>
            </Row>
          </Stack>
          <Stack size={2}>
            <Slider
              isDisabled={isCreating}
              minValue={-2}
              maxValue={2}
              value={interactivity}
              label={TRANSLATED_INTERACTIVITY}
              onChange={(value) => typeof value === 'number' && handleUpdateInteractivity(value)}
            />
            <Row justify="space-between">
              <Col xs="auto">
                <Body level="sm" color={theme.colors.secondary[600]}>
                  {TRANSLATED_INTERACTIVITY_LOWEST}
                </Body>
              </Col>
              <Col xs="auto">
                <Body level="sm" color={theme.colors.secondary[600]}>
                  {TRANSLATED_INTERACTIVITY_HIGHEST}
                </Body>
              </Col>
            </Row>
          </Stack>
          <Stack size={2}>
            {showAdditionalPrompt && (
              <TextField
                isDisabled={isCreating}
                label={`${TRANSLATED_ADDITIONAL_PROMPT} ${TRANSLATED_ADDITIONAL_PROMPT_NOTE}`}
                placeholder={TRANSLATED_ADDITIONAL_PROMPT_PLACEHOLDER}
                onChange={(text) => handleUpdateAdditionalPrompt(text)}
              />
            )}
            <Link href={FEEDBACK_LINK} target="_blank">
              {TRANSLATED_GIVE_FEEDBACK_LINK_TEXT}
            </Link>
          </Stack>
        </Stack>
      </CreateFromFileSettingsModalBody>
    </>
  );
}
